import { RefreshCw } from 'lucide-react';

import type { WeeklyStatistics } from '@/services';

const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

type ThisWeekWidgetProps = {
  statistics: WeeklyStatistics | null;
  loading: boolean;
  error: string;
  onRetry: () => void;
};

function LoadingState() {
  return (
    <div role="status" aria-label="Loading weekly statistics" className="animate-pulse">
      <div className="flex items-baseline gap-2">
        <div className="h-9 w-8 rounded bg-[#eef1ed]" />
        <div className="h-3.5 w-32 rounded bg-[#f1efea]" />
      </div>
      <div className="mt-2 h-3 w-36 rounded bg-[#f1efea]" />
      <div className="mt-3 flex h-9 items-end gap-1">
        {[10, 22, 14, 30, 18, 26, 20].map((height, index) => (
          <div
            key={index}
            className="flex-1 rounded-[3px] bg-[#eef4f0]"
            style={{ height: `${height}px` }}
          />
        ))}
      </div>
    </div>
  );
}

export function ThisWeekWidget({ statistics, loading, error, onRetry }: ThisWeekWidgetProps) {
  const dailyNewCards = statistics?.dailyNewCards ?? [];
  const maximumDailyCount = Math.max(1, ...dailyNewCards.map((day) => day.count));

  return (
    <div className="rounded-2xl border border-[#ece9e2] bg-white px-[18px] py-4 font-[family-name:var(--font-meaning-sans)]">
      <div className="mb-3 text-lg font-extrabold text-[#1e1c18]">This week</div>
      {loading ? (
        <LoadingState />
      ) : error || !statistics ? (
        <div role="alert" className="flex min-h-[82px] items-center justify-between gap-3">
          <span className="text-[13.5px] leading-5 text-[#8a8174]">
            {error || 'Unable to load weekly statistics.'}
          </span>
          <button
            type="button"
            onClick={onRetry}
            className="flex size-8 shrink-0 items-center justify-center rounded-full text-[#2f5c4e] transition hover:bg-[#eef4f0]"
            aria-label="Retry loading weekly statistics"
            title="Try again"
          >
            <RefreshCw className="size-4" />
          </button>
        </div>
      ) : (
        <>
          <div className="flex items-baseline gap-2">
            <span className="font-display text-[36px] font-semibold leading-none text-[#1e1c18]">
              {statistics.newCardsCount}
            </span>
            <span className="text-[15px] text-[#57534a]">new words this week</span>
          </div>
          <div className="mt-1 text-[14.5px] text-[#a39c8d]">
            {statistics.reviewsCount} reviews · {statistics.accuracyPercentage}% correct
          </div>
          <div
            className="mt-3 flex h-9 items-end gap-1"
            role="list"
            aria-label="New cards by day from Monday through Sunday"
          >
            {dailyNewCards.map((day, index) => {
              const height = day.count === 0
                ? 4
                : Math.max(8, Math.round(day.count / maximumDailyCount * 30));

              return (
                <div
                  key={day.date.toISOString()}
                  role="listitem"
                  className="flex-1 rounded-[3px] bg-[#eef4f0]"
                  style={{ height: `${height}px` }}
                  aria-label={`${dayNames[index] ?? 'Day'}: ${day.count} new cards`}
                  title={`${dayNames[index] ?? 'Day'}: ${day.count}`}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
